import React, {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';
import {
    Button,
    Flex,
    Dialog,
    DialogTitle,
    PriorityIcon
} from '@priority-software/priority-style-react';
import {ListQuestions} from '../../../components/ListQuestions';
import {MainQuestion} from '../../../components/MainQuestion';
import {isRTL} from '../../../../../Localization';
import {Footer} from '../../TabsContent/Footer';
import {Content} from '../../TabsContent/Content';
import {Loader} from '../../../components';
import {setQuestionnaireStep} from '../../../service/completionState';
import texts from '../../../texts';
import styles from './styles.module.scss';
import {sendMonitor} from '../../../../../utils/monitoring';

type Props = {
    showSubQuestion?: boolean;
    data: Array<any>;
    subjectId: string;
    onChange: (val: number, ScrLine: number) => void;
    onNextClick: (id: string, hideLoader?: boolean) => void;
    onPrevClick: (id: string) => void;
    isLastSubject?: boolean;
    isLoading?: boolean;
    finalizeQuiz: () => void;
    toggleStepSubQuestions: (flag: boolean) => void;
    submittable: boolean;
};

export const Question = (props: Props) => {
    const {
        data,
        onNextClick,
        onPrevClick,
        subjectId,
        onChange,
        showSubQuestion,
        isLastSubject,
        isLoading,
        finalizeQuiz,
        toggleStepSubQuestions,
        submittable
    } = props;

    const [isOpen, setOpen] = useState(false);

    const hasAdditionalQuestion = data.length > 1;
    const mainQuestionValue = data[0].value;
    const questions = hasAdditionalQuestion ? data.filter(q => q.isMain !== showSubQuestion) : data;

    useEffect(() => {
        (async () => await setQuestionnaireStep(subjectId))();
    }, [subjectId]);

    const openModal = useCallback(() => {
        sendMonitor('OnboardingTabsQuestionsDone');
        setOpen(true);
    }, [setOpen]);

    const closeModal = useCallback(() => setOpen(false), [setOpen]);

    const startProcessing = useCallback(async () => {
        sendMonitor('OnboardingRunOK');
        closeModal();
        await finalizeQuiz();
    }, [closeModal, finalizeQuiz]);

    const handleGoBack = () => {
        sendMonitor('OnboardingRunBack');
        closeModal();
    };

    const goNext = useCallback(() => {
        if (mainQuestionValue === 1 && !showSubQuestion && hasAdditionalQuestion) {
            toggleStepSubQuestions(true);
        } else {
            onNextClick(subjectId);
        }
    }, [
        showSubQuestion,
        hasAdditionalQuestion,
        mainQuestionValue,
        onNextClick,
        subjectId,
        toggleStepSubQuestions
    ]);

    const goPrev = useCallback(() => {
        if (showSubQuestion && hasAdditionalQuestion) {
            toggleStepSubQuestions(false);
        } else {
            onPrevClick(subjectId);
        }
    }, [showSubQuestion, hasAdditionalQuestion, onPrevClick, subjectId, toggleStepSubQuestions]);

    const questionView =
        questions.length > 1 ? (
            <ListQuestions onChange={onChange} data={questions} />
        ) : (
            questions.map(q => (
                <MainQuestion
                    key={q.questionID}
                    name={q.questionID}
                    onChange={onChange}
                    text={q.text}
                    title={q.question}
                    value={q.value}
                    ScrLine={q.ScrLine}
                />
            ))
        );

    const showNextButton = () => {
        if (isLastSubject) {
            /*
             * Show next button if the last subject Main Question is `positive` and:
             * 1) don't have additional sub-questions;
             * 2) sub-questions are not shown.
             */
            const haveSubQuestion = mainQuestionValue === 1 && !hasAdditionalQuestion;
            const subQuestionShown = mainQuestionValue === 1 && !showSubQuestion;

            return haveSubQuestion || subQuestionShown;
        }
        return !isLastSubject;
    };

    return (
        <>
            {isLoading ? (
                <div className={styles.contentLoader}>
                    <Loader componentClasses="onboarding-loader" width={80} height={80} />
                </div>
            ) : (
                <Content componentClasses={styles.questionWrap}>{questionView}</Content>
            )}

            <Footer>
                <Flex componentClasses={styles.pagination}>
                    <Button
                        onClick={goPrev}
                        skin="text"
                        prefixIcon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                        componentClasses={styles.btn}
                    >
                        {texts.quiz_prev_label}
                    </Button>

                    {showNextButton() ? (
                        <Button
                            onClick={goNext}
                            skin="text"
                            suffixIcon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                            componentClasses={styles.btn}
                        >
                            {texts.quiz_next_label}
                        </Button>
                    ) : (
                        <Button
                            disabled={!submittable}
                            onClick={openModal}
                            skin="primary"
                            size="medium"
                            componentClasses={cx(styles.btn, styles.submit)}
                        >
                            {texts.complete_label}
                        </Button>
                    )}
                </Flex>
            </Footer>

            <Dialog
                width={320}
                isOpen={isOpen}
                onClose={closeModal}
                size="small"
                title={
                    <DialogTitle
                        title={texts.workspace_dialog_title}
                        icon={PriorityIcon.moreInfo}
                    />
                }
                footer={
                    <Flex>
                        <Button onClick={startProcessing}>{texts.workspace_dialog_btn_ok}</Button>

                        <Button skin="secondary" onClick={handleGoBack}>
                            {texts.workspace_dialog_btn_back}
                        </Button>
                    </Flex>
                }
            />
        </>
    );
};
